/**
 * ProblemPage — OPEN-195 PR 1 (DEC-391). Operations → one problem: who / what / why.
 *
 * Opened from any Operations row that shows a problem (an area or a top problem on Health, an
 * Errors row). The filter is the URL (/ops/problem?event=&code=&category=&severity=&from=&to=),
 * so a refresh or a shared link shows the same drill; Back returns to the exact Operations URL
 * it came from (useBackTo). Reads GET /v1/platform/ops/problem, polling like the other panels.
 *
 *   header        the problem in plain words, its area, severity, code and window, and the totals
 *   explanation   what it means and the likely causes, most likely first, each with what to
 *                 check (the API ranks them from the numbers below; DEC-380's alarm-email tone)
 *   who           tenants, users, API keys and phones, top 10 each plus "others"
 *   what          routes, codes, HTTP statuses and a count per time slot
 *   samples       the newest 20 rows, with the Errors tab's "Raw log" link
 *
 * Ids are never shown: names, roles, the key's lxk_ handle, the phone's platform and version.
 */
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { formatNumber } from '@lodex-pro/shared/number-format';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Pill, subText } from '../../ui/bits';
import { useBackTo } from '../../lib/url-state';
import {
  OPS_PROBLEM_KEYS,
  useOpsProblem,
  type OpsCategory,
  type OpsProblem,
  type OpsProblemBreakdown,
  type OpsProblemFilter,
  type OpsProblemItem,
  type OpsSeverity,
} from './hooks';
import {
  OPS_CATEGORY_OPTIONS,
  categoryLabel,
  eventLabel,
  severityTone,
  statusLabel,
  timeAgo,
} from './labels';
import { rawLogTarget, type OpsLogState } from './OpsPage';

const SEVERITY_VALUES: readonly OpsSeverity[] = ['INFO', 'WARNING', 'ERROR', 'CRITICAL'];

const sectionTitle: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  margin: '24px 0 8px',
};

const linkButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-link)',
  cursor: 'pointer',
  fontSize: '13px',
};

/** The URL's filter, dropping anything the API would refuse anyway. Exported for the tests. */
export function problemFilterFrom(params: URLSearchParams): OpsProblemFilter {
  const out: Record<string, string> = {};
  for (const key of OPS_PROBLEM_KEYS) {
    const v = params.get(key);
    if (v !== null && v !== '') out[key] = v;
  }
  const category = out['category'];
  const severity = out['severity'];
  return {
    ...(out['event'] !== undefined ? { event: out['event'] } : {}),
    ...(out['code'] !== undefined ? { code: out['code'] } : {}),
    ...(category !== undefined && (OPS_CATEGORY_OPTIONS as readonly string[]).includes(category)
      ? { category: category as OpsCategory }
      : {}),
    ...(severity !== undefined && (SEVERITY_VALUES as readonly string[]).includes(severity)
      ? { severity: severity as OpsSeverity }
      : {}),
    ...(out['from'] !== undefined ? { from: out['from'] } : {}),
    ...(out['to'] !== undefined ? { to: out['to'] } : {}),
  };
}

const share = (n: number, total: number): string =>
  total > 0 ? `${String(Math.round((n / total) * 100))}%` : '—';

function windowLabel(w: OpsProblem['window']): string {
  const from = new Date(w.from);
  const to = new Date(w.to);
  const mins = Math.round((to.getTime() - from.getTime()) / 60_000);
  if (Math.abs(Date.now() - to.getTime()) < 5 * 60_000) {
    if (mins <= 61) return 'the last hour';
    if (mins <= 24 * 60 + 1) return 'the last 24 hours';
    return `the last ${String(Math.round(mins / (24 * 60)))} days`;
  }
  return `${from.toLocaleString()} to ${to.toLocaleString()}`;
}

/** How one row of a "who" table reads. */
type WhoKind = 'tenant' | 'user' | 'apiKey' | 'device';

const NONE_LABEL: Readonly<Record<WhoKind, string>> = {
  tenant: 'No tenant',
  user: 'No user (a system job or an API key)',
  apiKey: 'No API key',
  device: 'No phone',
};

function whoLabel(kind: WhoKind, i: OpsProblemItem): React.ReactNode {
  if (i.key === null) return <span style={subText}>{NONE_LABEL[kind]}</span>;
  switch (kind) {
    case 'tenant':
      return i.name ?? 'Tenant no longer exists';
    case 'user':
      return (
        <>
          {i.name ?? 'User no longer exists'}
          {i.role !== null && i.role !== undefined && <span style={subText}> · {i.role}</span>}
        </>
      );
    case 'apiKey':
      return (
        <>
          {i.name ?? 'Unknown key'}
          {i.keyPrefix !== null && i.keyPrefix !== undefined && (
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}> {i.keyPrefix}…</span>
          )}
          {i.status !== null && i.status !== undefined && i.status !== 'ACTIVE' && (
            <>
              {' '}
              <Pill
                label={`${i.status.charAt(0)}${i.status.slice(1).toLowerCase()}`}
                tone="warning"
              />
            </>
          )}
        </>
      );
    case 'device':
      return (
        <>
          {[
            i.platform === 'ios' ? 'iPhone' : i.platform === 'android' ? 'Android' : 'Phone',
            i.appVersion !== null && i.appVersion !== undefined ? `app ${i.appVersion}` : null,
            i.mode === 'SHARED' ? 'shared tablet' : null,
          ]
            .filter((x) => x !== null)
            .join(' · ')}
          {i.revoked === true && (
            <>
              {' '}
              <Pill label="App removed or signed out" tone="muted" />
            </>
          )}
        </>
      );
  }
}

/** A breakdown as a small table: label, times, share; "others" as the last row. */
function BreakdownTable({
  title,
  b,
  total,
  label,
  emptyText = 'None recorded.',
}: {
  title: string;
  b: OpsProblemBreakdown;
  total: number;
  label: (i: OpsProblemItem) => React.ReactNode;
  emptyText?: string;
}): React.JSX.Element {
  const known = b.items.some((i) => i.key !== null);
  return (
    <div style={{ flex: 1, minWidth: '280px' }}>
      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '12px 0 6px' }}>{title}</h4>
      {!known ? (
        <p style={subText}>{emptyText}</p>
      ) : (
        <Table aria-label={title}>
          <TableHeader>
            <TableRow>
              <TableHead>{title}</TableHead>
              <TableHead>Times</TableHead>
              <TableHead>Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {b.items.map((i) => (
              <TableRow key={i.key ?? '∅'}>
                <TableCell>{label(i)}</TableCell>
                <TableCell>{formatNumber(i.occurrences)}</TableCell>
                <TableCell>{share(i.occurrences, total)}</TableCell>
              </TableRow>
            ))}
            {b.othersKeys > 0 && (
              <TableRow>
                <TableCell>
                  <span style={subText}>{formatNumber(b.othersKeys)} others</span>
                </TableCell>
                <TableCell>{formatNumber(b.othersOccurrences)}</TableCell>
                <TableCell>{share(b.othersOccurrences, total)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/** Counts per time slot as plain bars (no chart library). Exported for the tests. */
export function OverTime({ p }: { p: OpsProblem }): React.JSX.Element {
  if (p.buckets.length === 0) return <p style={subText}>Nothing in this window.</p>;
  const max = Math.max(...p.buckets.map((b) => b.occurrences), 1);
  const daily = p.bucketSeconds >= 24 * 3600;
  const fmt = (iso: string): string => {
    const d = new Date(iso);
    return daily
      ? d.toLocaleDateString()
      : d.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  };
  return (
    <ul aria-label="Over time" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {p.buckets.map((b) => (
        <li
          key={b.at}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}
        >
          <span style={{ width: '140px', flexShrink: 0 }}>{fmt(b.at)}</span>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              height: '10px',
              width: `${String(Math.max(2, Math.round((b.occurrences / max) * 240)))}px`,
              backgroundColor: 'var(--color-accent-blue)',
              borderRadius: '2px',
            }}
          />
          <span>{formatNumber(b.occurrences)}</span>
        </li>
      ))}
    </ul>
  );
}

function ProblemView({ p }: { p: OpsProblem }): React.JSX.Element {
  const navigate = useNavigate();
  const total = p.totals.occurrences;
  const f = p.filter;
  const title =
    f.event !== null
      ? eventLabel(f.event)
      : f.category !== null
        ? `Problems in ${categoryLabel(f.category)}`
        : 'Problems';
  const openRawLog = (e: OpsProblem['samples'][number]): void => {
    const t = rawLogTarget(e);
    if (t === null) return;
    const state: OpsLogState = { logTarget: t };
    void navigate('/ops?tab=logs', { state });
  };

  return (
    <>
      <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '12px 0 4px' }}>{title}</h1>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        {f.category !== null && <Pill label={categoryLabel(f.category)} tone="neutral" />}
        {f.severity !== null && <Pill label={f.severity} tone={severityTone(f.severity)} />}
        {f.code !== null && (
          <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{f.code}</span>
        )}
        <span style={subText}>In {windowLabel(p.window)}</span>
      </div>
      <p aria-label="Totals" style={{ fontSize: '14px', margin: '8px 0 0' }}>
        <strong>{formatNumber(total)}</strong> time{total === 1 ? '' : 's'} in{' '}
        {formatNumber(p.totals.rows)} stored row
        {p.totals.rows === 1 ? '' : 's'}
        {p.totals.firstSeen !== null && <> · first {timeAgo(p.totals.firstSeen)}</>}
        {p.totals.lastSeen !== null && <>, last {timeAgo(p.totals.lastSeen)}</>}
        {' · '}
        {formatNumber(p.totals.tenants)} tenant{p.totals.tenants === 1 ? '' : 's'},{' '}
        {formatNumber(p.totals.users)} user
        {p.totals.users === 1 ? '' : 's'}
        {p.totals.apiKeys > 0 && (
          <>
            , {formatNumber(p.totals.apiKeys)} API key{p.totals.apiKeys === 1 ? '' : 's'}
          </>
        )}
        {p.totals.devices > 0 && (
          <>
            , {formatNumber(p.totals.devices)} phone{p.totals.devices === 1 ? '' : 's'}
          </>
        )}
      </p>

      <h3 style={sectionTitle}>Likely explanation</h3>
      <section aria-label="Likely explanation">
        <p style={{ fontSize: '14px', margin: '0 0 8px' }}>{p.explanation.meaning}</p>
        <div style={{ ...subText, textTransform: 'none', marginBottom: '4px' }}>
          Most likely causes
        </div>
        <ol aria-label="Most likely causes" style={{ margin: '0 0 8px 20px', fontSize: '14px' }}>
          {p.explanation.causes.map((c) => (
            <li key={c.cause} style={{ marginBottom: '6px' }}>
              <div>{c.cause}</div>
              <div style={subText}>Check: {c.check}</div>
            </li>
          ))}
        </ol>
        {p.explanation.rankedBecause !== null && (
          <p style={{ ...subText, textTransform: 'none' }}>{p.explanation.rankedBecause}</p>
        )}
      </section>

      <h3 style={sectionTitle}>Who</h3>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <BreakdownTable
          title="Tenants"
          b={p.byTenant}
          total={total}
          label={(i): React.ReactNode => whoLabel('tenant', i)}
        />
        <BreakdownTable
          title="Users"
          b={p.byUser}
          total={total}
          label={(i): React.ReactNode => whoLabel('user', i)}
        />
        <BreakdownTable
          title="API keys"
          b={p.byApiKey}
          total={total}
          label={(i): React.ReactNode => whoLabel('apiKey', i)}
          emptyText="Not an Operator API call."
        />
        <BreakdownTable
          title="Phones"
          b={p.byDevice}
          total={total}
          label={(i): React.ReactNode => whoLabel('device', i)}
          emptyText="No phone involved."
        />
      </div>

      <h3 style={sectionTitle}>What</h3>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <BreakdownTable
          title="Routes"
          b={p.byRoute}
          total={total}
          label={(i): React.ReactNode => (
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{i.key}</span>
          )}
          emptyText="Not a request (a background job or a phone sync)."
        />
        <BreakdownTable
          title="Codes"
          b={p.byCode}
          total={total}
          label={(i): React.ReactNode => (
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{i.key}</span>
          )}
        />
        <BreakdownTable
          title="Status codes"
          b={p.byStatus}
          total={total}
          label={(i): React.ReactNode => statusLabel(i.key ?? '')}
        />
      </div>
      <h4 style={{ fontSize: '14px', fontWeight: 600, margin: '12px 0 6px' }}>
        Over time (per{' '}
        {p.bucketSeconds >= 3600
          ? `${String(p.bucketSeconds / 3600)} h`
          : `${String(p.bucketSeconds / 60)} min`}
        )
      </h4>
      <OverTime p={p} />

      <h3 style={sectionTitle}>Newest {p.samples.length}</h3>
      {p.samples.length === 0 ? (
        <p style={subText}>Nothing in this window.</p>
      ) : (
        <Table aria-label="Samples">
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Detail</TableHead>
              <TableHead>Ref</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {p.samples.map((e) => (
              <TableRow key={e.id}>
                <TableCell>
                  <div>{new Date(e.occurredAt).toLocaleString()}</div>
                  {e.occurrences > 1 && (
                    <div style={subText}>
                      ×{formatNumber(e.occurrences)}, last {timeAgo(e.lastOccurredAt)}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Pill label={e.severity} tone={severityTone(e.severity)} />
                </TableCell>
                <TableCell>{e.tenantName ?? <span style={subText}>—</span>}</TableCell>
                <TableCell>
                  <div style={subText}>
                    {[e.code, e.statusCode === null ? null : String(e.statusCode), e.role]
                      .filter((x) => x !== null)
                      .join(' · ')}
                  </div>
                  {e.route !== null && (
                    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{e.route}</div>
                  )}
                  {e.message !== null && <div style={subText}>{e.message}</div>}
                </TableCell>
                <TableCell>
                  {e.refCode !== null && <div style={{ fontFamily: 'monospace' }}>{e.refCode}</div>}
                  {rawLogTarget(e) !== null && (
                    <button type="button" style={linkButton} onClick={(): void => openRawLog(e)}>
                      Raw log
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

export function ProblemPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const filter = problemFilterFrom(params);
  const back = useBackTo('/ops');
  const { data, isLoading, isError } = useOpsProblem(filter);
  const usable = filter.event !== undefined || filter.category !== undefined;
  return (
    <div style={{ padding: '24px', maxWidth: '1200px' }}>
      <Link to={back} style={{ ...linkButton, textDecoration: 'none' }}>
        ← Operations
      </Link>
      {!usable ? (
        <p role="alert" style={subText}>
          This link does not name a problem. Open one from Operations.
        </p>
      ) : isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={subText}>
          Could not load this problem.
        </p>
      ) : (
        <ProblemView p={data} />
      )}
    </div>
  );
}
